import { useState } from 'react'
import { useAuth } from '@/modules/auth'
import { UserProfileApi } from '@/shared/api/users'
import { Button } from '@/ui/components'
import { MapPin } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/ui/components/primitives/dialog'
import { LocationSearchInput, type LocationSearchResult } from '@/modules/trips/components/forms/LocationSearchInput'

interface SetHomeBaseModalProps {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

/** One-time capture of a user's home base — feeds the weekly weekend-suggestion cron, which runs with no browser session to read a live location from. */
export function SetHomeBaseModal({ open, onClose, onSaved }: SetHomeBaseModalProps) {
  const { user, getAccessToken } = useAuth()
  const [locationText, setLocationText] = useState('')
  const [selected, setSelected] = useState<LocationSearchResult | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!selected || !user?.id) return
    setSaving(true)
    setError(null)
    try {
      const api = new UserProfileApi(getAccessToken)
      await api.updateUserProfile(user.id, {
        home_latitude: selected.latitude,
        home_longitude: selected.longitude,
        home_location_label: selected.address || selected.name,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save home base. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-adventure-600" />
            Set your home base
          </DialogTitle>
          <DialogDescription>
            Ranger uses this to find weekend camping spots near you — nothing else changes on your profile.
          </DialogDescription>
        </DialogHeader>

        <LocationSearchInput
          value={locationText}
          onChange={setLocationText}
          onLocationSelect={setSelected}
          placeholder="Search your city or address..."
        />

        {error && (
          <p className="text-sm text-red-600 rounded-md bg-red-50 px-3 py-2">{error}</p>
        )}

        <Button
          className="w-full bg-adventure-600 hover:bg-adventure-700 text-white"
          onClick={handleSave}
          disabled={!selected || saving}
        >
          {saving ? 'Saving…' : 'Save home base'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
